import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { isRole, isRoleType } from "../../redux/selectors/authSelector";
import Loader from "../../components/common/Loader";
import { useCountryNavigate } from "../../components/common/UseCountryNavigate";
import { toast } from "sonner";

const getStatusBadge = (status) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    sent: "bg-green-100 text-green-800",
    partially_sent: "bg-orange-100 text-orange-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-800",
  };
  return map[status] || "bg-gray-100 text-gray-600";
};

const getModuleLabel = (module) => {
  switch (module) {
    case "parent": return "Parents";
    case "school_management": return "School Management";
    case "board": return "Board Members";
    case "teacher": return "Teachers";
    default: return module || "-";
  }
};

const getRecipientName = (item, module) => {
  const r = item.recipient;
  if (r && r.name) return r.name;
  // Fallback for parent_id (legacy)
  if (item.parent_id?.name) return item.parent_id.name;
  return "-";
};

const getRecipientMobile = (item, module) => {
  // Direct mobile_number from the sent/failed entry
  if (item.mobile_number) return item.mobile_number;
  const r = item.recipient;
  if (r) {
    if (module === "parent") return r.mobile_number || "";
    return r.mobile || "";
  }
  // Fallback
  if (item.parent_id?.mobile_number) return item.parent_id.mobile_number;
  return "-";
};

const ViewCommunication = () => {
  const { id } = useParams();
  const navigate = useCountryNavigate();
  const [loader, setLoader] = useState(true);
  const [communication, setCommunication] = useState(null);
  const [activeDeliveryTab, setActiveDeliveryTab] = useState("sent");

  const role = useSelector(isRole);
  const role_type = useSelector(isRoleType);
  const mode = localStorage.getItem("mode");
  const schoolId = localStorage.getItem("activeSchoolId");

  useEffect(() => {
    const fetchCommunication = async () => {
      try {
        const res = await Postdata(HttpService.communication_show, { id });

        if (res.status === 200) {
          setCommunication(res.data);
        } else {
          toast.error("Communication not found");
        }
      } catch (err) {
        toast.error("Failed to fetch communication details");
      } finally {
        setLoader(false);
      }
    };

    fetchCommunication();
  }, [id]);

  const handleBackNavigation = () => {
    if (
      role === "admin" ||
      role === "subadmin" ||
      role_type === "schoolsubadmin" ||
      role_type === "subadmin"
    ) {
      if (mode === "view") {
        navigate(`admin-school/view/${schoolId}`, {
          state: { mainTab: "Communication" },
        });
      } else {
        navigate("navigation-dashboard", {
          state: { mainTab: "Communication" },
        });
      }
    } else {
      navigate("communication");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loader) return <Loader isClass="loader_fullscreen" />;
  if (!communication) return null;

  const sentList = communication.sent_to || [];
  const failedList = communication.failed_to || [];

  return (
    <div className="p-6 space-y-6 pr-0">
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="w-full bg-white rounded-xl p-6">
          {/* Header */}
          <div
            className="flex items-center mb-6 cursor-pointer"
            onClick={handleBackNavigation}
          >
            <FiArrowLeft className="text-2xl text-gray-800 mr-2" />
            <h2 className="text-2xl font-bold text-gray-800">
              Communication Details
            </h2>
          </div>

          {/* Basic Info */}
          <div className="space-y-5">
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Message
              </label>
              <div className="mt-1 w-full border border-gray-300 rounded-lg p-3 bg-gray-100 whitespace-pre-wrap">
                {communication.message}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <input
                  type="text"
                  disabled
                  value={
                    communication.type === "instant" ? "Instant" : "Scheduled"
                  }
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100 capitalize"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <div className="mt-1">
                  <span
                    className={`inline-block px-3 py-2 rounded-lg text-sm font-medium capitalize ${getStatusBadge(
                      communication.status
                    )}`}
                  >
                    {communication.status?.replace("_", " ") || "-"}
                  </span>
                </div>
              </div>

              {/* Recipient Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Recipient Type
                </label>
                <input
                  type="text"
                  disabled
                  value={getModuleLabel(communication.recipient_module)}
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100"
                />
              </div>

              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target
                </label>
                <input
                  type="text"
                  disabled
                  value={
                    communication.target === "all"
                      ? `All ${getModuleLabel(communication.recipient_module)}`
                      : `Selected ${getModuleLabel(communication.recipient_module)}`
                  }
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100"
                />
              </div>

              {/* Total Recipients */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Total Recipients
                </label>
                <input
                  type="text"
                  disabled
                  value={communication.total_recipients ?? "-"}
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100"
                />
              </div>

              {/* Credits Used */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Credits Used
                </label>
                <input
                  type="text"
                  disabled
                  value={communication.credits_used ?? "-"}
                  className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100"
                />
              </div>

              {/* Scheduled Time */}
              {communication.type === "scheduled" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Scheduled Time
                  </label>
                  <input
                    type="text"
                    disabled
                    value={formatDate(communication.scheduled_at)}
                    className="mt-1 w-full border border-gray-300 rounded-lg p-2 bg-gray-100"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Delivery Tracking */}
          <div className="mt-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              Delivery Tracking
            </h3>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveDeliveryTab("sent")}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeDeliveryTab === "sent"
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Sent ({sentList.length})
              </button>
              <button
                onClick={() => setActiveDeliveryTab("failed")}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeDeliveryTab === "failed"
                    ? "bg-red-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Failed ({failedList.length})
              </button>
            </div>

            {/* Sent List */}
            {activeDeliveryTab === "sent" && (
              <div className="bg-white rounded border overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="text-gray-700 bg-green-50">
                    <tr>
                      <th className="px-4 py-3 font-medium">#</th>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Mobile Number</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sentList.length === 0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center py-4 text-gray-400"
                        >
                          No sent records
                        </td>
                      </tr>
                    ) : (
                      sentList.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{index + 1}</td>
                          <td className="px-4 py-3">
                            {getRecipientName(item, communication.recipient_module)}
                          </td>
                          <td className="px-4 py-3">
                            {getRecipientMobile(item, communication.recipient_module)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Failed List */}
            {activeDeliveryTab === "failed" && (
              <div className="bg-white rounded border overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="text-gray-700 bg-red-50">
                    <tr>
                      <th className="px-4 py-3 font-medium">#</th>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Mobile Number</th>
                      <th className="px-4 py-3 font-medium">Failure Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {failedList.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="text-center py-4 text-gray-400"
                        >
                          No failed records
                        </td>
                      </tr>
                    ) : (
                      failedList.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{index + 1}</td>
                          <td className="px-4 py-3">
                            {getRecipientName(item, communication.recipient_module)}
                          </td>
                          <td className="px-4 py-3">
                            {getRecipientMobile(item, communication.recipient_module)}
                          </td>
                          <td className="px-4 py-3 text-red-600">
                            {item.reason || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCommunication;
